import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Send, Store, DollarSign, Inbox, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import useAuthStore from '../lib/auth-store';
import { apiFetch, fmtDate, money, isAdmin } from '../components/merchant/attributionApi';
import { DateField } from '../components/merchant/DateField';

function InviteCard({ p, token, reload }) {
  const [link, setLink] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const invite = async () => {
    setBusy(true);
    try {
      const res = await apiFetch(token, `/affiliates/${p.affiliate_id}/invite`, 'POST', email ? { email } : {});
      setLink(`${window.location.origin}/affiliate/accept/${res.token}`);
      toast.success('Invite link created — share it with the partner');
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const pct = Math.min(100, (p.referral_count / p.invite_threshold) * 100);
  return (
    <Card className="p-5 space-y-3" data-testid="partner-invite-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Partner portal</h3>
        <Badge variant="outline" className="capitalize" data-testid="partner-account-status">{p.account_status}</Badge>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Referred merchants</span><span data-testid="partner-referral-progress">{p.referral_count}/{p.invite_threshold}</span>
        </div>
        <Progress value={pct} />
      </div>
      {p.account_status === 'active' ? (
        <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Portal account active</p>
      ) : p.invite_eligible ? (
        <>
        {!p.email && <Input type="email" placeholder="Partner email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="partner-invite-email" />}
        <Button size="sm" onClick={invite} disabled={busy} data-testid="partner-invite-btn"><Send className="w-3.5 h-3.5 mr-1" /> {p.account_status === 'invited' ? 'Create new invite link' : 'Invite to portal'}</Button>
        </>
      ) : (
        <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="partner-invite-locked"><Lock className="w-3.5 h-3.5" /> Unlocks after {p.invite_threshold} referred merchants</p>
      )}
      {link && (
        <div className="flex gap-2">
          <Input readOnly value={link} className="text-xs" data-testid="partner-invite-link" />
          <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success('Copied'); }} data-testid="partner-invite-copy"><Copy className="w-4 h-4" /></Button>
        </div>
      )}
    </Card>
  );
}

const L = ({ t, children }) => <label className="space-y-1 block"><span className="text-xs text-muted-foreground">{t}</span>{children}</label>;

function AgreementCard({ p, token, reload, admin }) {
  const cur = p.statement?.current_agreement;
  const [f, setF] = useState({ residual_pct: '', bounty_per_approved_mid: '', effective_from: '', reason: '' });
  const save = async () => {
    try {
      await apiFetch(token, `/affiliates/${p.affiliate_id}/agreements`, 'POST', {
        residual_pct: Number(f.residual_pct || 0), bounty_per_approved_mid: Number(f.bounty_per_approved_mid || 0),
        effective_from: f.effective_from ? new Date(f.effective_from).toISOString() : undefined, reason: f.reason || undefined,
      });
      toast.success('Agreement saved and commissions accrued');
      setF({ residual_pct: '', bounty_per_approved_mid: '', effective_from: '', reason: '' });
      reload();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="partner-agreement-card">
      <h3 className="font-semibold text-sm flex items-center gap-2"><DollarSign className="w-4 h-4" /> Compensation agreement</h3>
      {cur ? (
        <div className="grid grid-cols-2 gap-2 text-sm" data-testid="partner-current-agreement">
          <div><p className="text-xs text-muted-foreground">Residual share</p><p className="font-semibold">{cur.residual_pct}%</p></div>
          <div><p className="text-xs text-muted-foreground">Bounty / approved MID</p><p className="font-semibold">{money(cur.bounty_per_approved_mid)}</p></div>
          <p className="col-span-2 text-xs text-muted-foreground">v{cur.version} · since {fmtDate(cur.effective_from)}</p>
        </div>
      ) : <p className="text-xs text-muted-foreground" data-testid="partner-no-agreement">No agreement yet — nothing accrues until one is set.</p>}
      {admin && (
        <div className="grid grid-cols-2 gap-2 pt-2 border-t">
          <L t="Residual share %"><Input type="number" placeholder="e.g. 10" value={f.residual_pct} onChange={(e) => setF({ ...f, residual_pct: e.target.value })} data-testid="agreement-residual-pct" /></L>
          <L t="Bounty per approved MID ($)"><Input type="number" placeholder="e.g. 150" value={f.bounty_per_approved_mid} onChange={(e) => setF({ ...f, bounty_per_approved_mid: e.target.value })} data-testid="agreement-bounty" /></L>
          <L t="Effective from (defaults to now)"><DateField value={f.effective_from} onChange={(v) => setF({ ...f, effective_from: v })} testId="agreement-effective" /></L>
          <L t={cur ? 'Reason (required)' : 'Reason (optional)'}><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="agreement-reason" /></L>
          <Button size="sm" className="col-span-2" onClick={save} data-testid="agreement-save-btn">{cur ? 'Replace agreement' : 'Set agreement'}</Button>
        </div>
      )}
    </Card>
  );
}

export function ReferredTable({ rows, testId = 'partner-referrals' }) {
  if (!rows?.length) return <p className="text-sm text-muted-foreground py-4" data-testid={`${testId}-empty`}>No referred merchants yet</p>;
  return (
    <div className="divide-y" data-testid={testId}>
      {rows.map((m) => (
        <div key={m.merchant_group_id} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`${testId}-row-${m.merchant_group_id}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Store className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="font-medium text-sm truncate">{m.name}</span>
            <span className="text-xs text-muted-foreground">since {fmtDate(m.referred_since)}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {m.mids.length === 0 && <Badge variant="outline">No MID yet</Badge>}
            {m.mids.map((x) => <Badge key={x.processing_account_id} variant="secondary" className="capitalize">{x.processor} {x.mid_last4 ? `…${x.mid_last4}` : ''} · {x.status}</Badge>)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CommissionLines({ lines, totals, testId = 'partner-commissions', selected, onToggle }) {
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-4 mb-3">
        {['pending', 'approved', 'paid'].map((s) => (
          <div key={s}><p className="text-xs text-muted-foreground capitalize">{s}</p><p className="font-semibold" data-testid={`${testId}-total-${s}`}>{money(totals?.[s])}</p></div>
        ))}
      </div>
      {!lines?.length ? <p className="text-sm text-muted-foreground">No commission lines yet</p> : (
        <div className="divide-y max-h-72 overflow-auto">
          {lines.map((l) => (
            <div key={l.commission_id} className="py-2 flex justify-between text-sm gap-2">
              <span className="truncate flex items-center gap-2">
                {onToggle && l.status !== 'paid' && <Checkbox checked={selected.includes(l.commission_id)} onCheckedChange={() => onToggle(l)} data-testid={`commission-select-${l.commission_id}`} />}
                {l.product}{l.mid ? ` · ${String(l.mid).slice(-4)}` : ''}</span>
              <span className="flex items-center gap-2 shrink-0"><Badge variant="outline" className="capitalize">{l.status}</Badge>{money(l.commission_amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PayoutLedger({ p, token, admin, reload }) {
  const [sel, setSel] = useState([]);
  const lines = p.statement?.lines || [];
  const toggle = (l) => setSel(sel.includes(l.commission_id) ? sel.filter((x) => x !== l.commission_id) : [...sel, l.commission_id]);
  const bulk = async (status) => {
    try {
      const r = await apiFetch(token, `/affiliates/${p.affiliate_id}/commissions/bulk-status`, 'POST', { commission_ids: sel, status });
      toast.success(`${r.updated} line(s) marked ${status}${r.skipped ? ` · ${r.skipped} skipped (wrong stage)` : ''}`);
      setSel([]); reload();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="p-5" data-testid="payout-ledger">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-sm">Commission ledger</h3>
        {admin && lines.length > 0 && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSel(lines.filter((l) => l.status !== 'paid').map((l) => l.commission_id))} data-testid="payout-select-all">Select open</Button>
            <Button size="sm" disabled={!sel.length} onClick={() => bulk('approved')} data-testid="payout-approve-btn">Approve</Button>
            <Button size="sm" variant="secondary" disabled={!sel.length} onClick={() => bulk('paid')} data-testid="payout-paid-btn">Mark paid</Button>
          </div>
        )}
      </div>
      <CommissionLines lines={lines} totals={p.statement?.totals} selected={sel} onToggle={admin ? toggle : undefined} />
    </Card>
  );
}

export default function ReferralPartnerPage() {
  const { affiliateId } = useParams();
  const { token, user } = useAuthStore();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState('');
  const load = useCallback(() => apiFetch(token, `/affiliates/${affiliateId}/profile`).then(setP).catch((e) => setMissing(e.message)), [token, affiliateId]);
  useEffect(() => { load(); }, [load]);
  if (missing && !p) {
    return (
      <div className="py-16 text-center space-y-3" data-testid="partner-not-found">
        <p className="font-medium">{missing}</p>
        <Link to="/affiliates" className="text-sm text-primary hover:underline">Back to affiliates</Link>
      </div>
    );
  }
  if (!p) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="referral-partner-page">
      <Link to="/affiliates" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Affiliates</Link>
      <div>
        <h1 className="text-2xl font-bold" data-testid="partner-name">{p.name}</h1>
        <p className="text-sm text-muted-foreground">{[p.company, p.email, p.phone, 'outside your organisation chart'].filter(Boolean).join(' · ')}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <InviteCard p={p} token={token} reload={load} />
        <div className="lg:col-span-2"><AgreementCard p={p} token={token} reload={load} admin={isAdmin(user?.role)} /></div>
      </div>
      <Card className="p-5"><h3 className="font-semibold text-sm mb-2">Referred merchants</h3><ReferredTable rows={p.referred_merchants} /></Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PayoutLedger p={p} token={token} admin={isAdmin(user?.role)} reload={load} />
        <Card className="p-5" data-testid="partner-leads">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Inbox className="w-4 h-4" /> Leads submitted from the portal</h3>
          {!p.leads?.length ? <p className="text-sm text-muted-foreground">None yet</p> : p.leads.map((l) => (
            <Link key={l.id} to={`/leads/${l.id}`} className="flex justify-between py-2 border-b last:border-0 text-sm hover:text-primary">
              <span>{l.business_name}</span><Badge variant="outline" className="capitalize">{l.status}</Badge>
            </Link>
          ))}
        </Card>
      </div>
    </div>
  );
}
